import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { DeleteOutline, RemoveRedEyeOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import withComponentAuth from '../../hoc/withComponentAuth';
import { SmartFilter } from '../../components/common/SmartFilter';
import Pagination from '../../components/atoms/Pagination';
import { ListTable } from '../../components/molecules/ListTable';
import { GETTechnicians, SoftDeleteTechnician } from '../../services/technician';
import { ITechnician } from '../../utils/entities/interface';
import { capitalizeWords, debounce, formatDate } from '../../utils/helper';
import useSnackbar from '../../hooks/useSnackbar';
import { usePaginationParams } from '../../hooks/usePaginationParams';
import { useScrollRestoration } from '../../hooks/useScrollRestoration';

const newJoinHeaderCells: [string, number][] = [
  ['Name', 100],
  ['Contact Number', 100],
  ['DOB', 80],
  ['Type', 70],
  ['Status', 70],
  ['Action', 50],
];

const getTechnicianKycStatus = (technician: ITechnician) => {
  const status =
    technician.kycStatus ||
    (technician as any).status ||
    (technician as any).verificationStatus ||
    'Pending';

  return String(status);
};

const BLOCKED_WORK_STATUSES = [
  'SIGNED_UP',
  'PROFILE_CREATED',
  'KYC_PENDING',
  'ON_BOARDING',
  'DISABLED',
  'TERMINATED',
  'RESIGNED',
];

const isNewJoinTechnician = (technician: ITechnician) => {
  const kycStatus = getTechnicianKycStatus(technician).trim().toUpperCase();
  const position = String(technician.position || '').trim().toUpperCase();
  const status = String(technician.status || '').trim().toUpperCase();

  return (
    kycStatus !== 'VERIFIED' ||
    position === 'TBA' ||
    BLOCKED_WORK_STATUSES.includes(status)
  );
};

const NewJoinTechnicians = () => {
  const navigate = useNavigate();
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const { page, rowsPerPage, setPage, setRowsPerPage } = usePaginationParams();
  const [allTechnicians, setAllTechnicians] = useState<ITechnician[]>([]);
  const [totalTechnician, setTotalTechnician] = useState(0);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const scrollRef = useScrollRestoration<HTMLDivElement>(!isLoading);
  const [deleteTarget, setDeleteTarget] = useState<ITechnician | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchTechnicians(searchQuery);
  }, [searchQuery]);

  const fetchTechnicians = async (searchQuery?: string) => {
    try {
      setIsLoading(true);

      const response = await GETTechnicians({
        page: 1,
        limit: 10000,
        searchQuery,
        sortField: 'joiningDate',
        sortOrder: 'DESC',
      });

      const filteredTechnicians = (response.data || []).filter(
        (technician: ITechnician) => isNewJoinTechnician(technician),
      );

      setAllTechnicians(filteredTechnicians);
      setTotalTechnician(filteredTechnicians.length);
    } catch (error) {
      console.error('Failed to fetch new join technicians:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const technicians = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;

    return allTechnicians.slice(startIndex, endIndex);
  }, [allTechnicians, page, rowsPerPage]);

  const handleChangePage = (_event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await SoftDeleteTechnician(deleteTarget._id);
      setAllTechnicians((prev) =>
        prev.filter((technician) => technician._id !== deleteTarget._id),
      );
      setTotalTechnician((prev) => Math.max(0, prev - 1));
      showSnackbar('Technician deleted successfully', 'success');
      setDeleteTarget(null);
    } catch (error: any) {
      showSnackbar(error?.message || 'Failed to delete technician', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}
      <SmartFilter
        title="New Join Technicians"
        createFilter={['SEARCH']}
        addDrawerSize="lg"
        filterdrawerSize="sm"
        filterHeader="Filter New Join"
        addHeader="New Join"
        onSearch={handleSearchRefresh}
      />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
      <Box
        ref={scrollRef}
        sx={{
          height: '100%',
          maxHeight: '100%',
          overflowY: 'scroll',
          scrollbarWidth: 'none',
        }}
      >
        <ListTable
          headerCells={newJoinHeaderCells}
          rows={technicians}
          isLoading={isLoading}
          noDataText="No New Join Technicians Found"
          actions={[
            {
              icon: <RemoveRedEyeOutlined />,
              onClick: (technician: ITechnician) =>
                navigate(`/technicians/${technician._id}`),
            },
            {
              icon: <DeleteOutline />,
              onClick: (technician: ITechnician) => setDeleteTarget(technician),
            },
          ]}
          renderCell={(technician: ITechnician, colIndex: number) => {
            switch (colIndex) {
              case 0:
                return capitalizeWords(technician.name) || 'N/A';

              case 1:
                return technician.phoneNumber
                  ? `${technician.countryCode || '+91'}-${technician.phoneNumber.slice(0, 10)}`
                  : 'N/A';

              case 2:
                return formatDate((technician as any).dob) || 'N/A';

              case 3:
                return technician.type || 'N/A';

              case 4: {
                const status = getTechnicianKycStatus(technician);

                return (
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1.8,
                      py: 0.8,
                      minWidth: '90px',
                      border: '1.5px solid #E31E25',
                      borderRadius: '9px',
                      color: '#9E9E9E',
                      fontSize: '13px',
                      fontWeight: 400,
                      lineHeight: 1,
                      cursor: 'default',
                      pointerEvents: 'none',
                      userSelect: 'none',
                      backgroundColor: 'transparent',
                    }}
                  >
                    {capitalizeWords(status)}
                  </Box>
                );
              }

              default:
                return null;
            }
          }}
        />
      </Box>

      <Box display="block" bottom={0} right={0}>
        <Pagination
          count={totalTechnician}
          page={page - 1}
          rowsPerPage={rowsPerPage}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
      </Box>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: '#171717',
            border: '1px solid #242424',
            color: '#fff',
          },
        }}
      >
        <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              border: '2px solid #E31E25',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <DeleteOutline sx={{ fontSize: 36, color: '#ef4444' }} />
          </Box>
          <Typography variant="h6" mb={1}>
            Delete technician?
          </Typography>
          <Typography variant="body2" color="#94a3b8">
            {(deleteTarget?.name && capitalizeWords(deleteTarget.name)) ||
              'This technician'}{' '}
            will be
            removed from all lists. This can be undone by restoring them
            later.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'center', gap: 1 }}>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleting}
            sx={{ color: '#94a3b8' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDelete}
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withComponentAuth(NewJoinTechnicians);
